import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectModuleBrands, selectSolarModules, selectInverterBrands, selectInverters, selectOptimizers, selectCombiners, selectRsds, selectBatteryBrands, selectBatteries, selectIsInitialized, selectMspBusses, selectMspBreakers, selectMspManufacturers, selectMspLocations, selectMspStyles, selectMspTypes, selectPoi, selectPoiLocations, selectAtsBreakerSizes, selectDisco, selectConduitSizes, selectConduitTypes, selectStructuralFramingSizes, selectStructuralFramingTypes, selectStructuralRafterSpacings, selectAttachments, selectRails, selectRoofTypes, selectRoofConditions, selectReroofStatuses, selectBuildingHeights, selectElectricConfigs, selectSettings } from '../../store/selectors/databaseSelectors';
import { fetchAllDatabaseData } from '../../store/slices/databaseSlice';
import { SettingsSection, getEnabledSettingsSections, SETTINGS_ICON_PATHS } from '../../constants';
import { ModuleManagement } from './components/ModuleManagement';
import { InverterManagement } from './components/InverterManagement';
import { OptimizerManagement } from './components/OptimizerManagement';
import { CombinerManagement } from './components/CombinerManagement';
import { RsdManagement } from './components/RsdManagement';
import { BatteryManagement } from './components/BatteryManagement';
import { MspManagement } from './components/MspManagement';
import { PoiManagement } from './components/PoiManagement';
import { DiscoManagement } from './components/DiscoManagement';
import { ConduitManagement } from './components/ConduitManagement';
import { StructuralManagement } from './components/StructuralManagement';
import { AttachmentRailManagement } from './components/AttachmentRailManagement';
import { RoofManagement } from './components/RoofManagement';
import { BuildingHeightManagement } from './components/BuildingHeightManagement';
import { ElectricConfigManagement } from './components/ElectricConfigManagement';
import { SettingsManagement } from './components/SettingsManagement';
import { ClientLogosManagement } from './components/ClientLogosManagement';

const AdminIcon: React.FC<{ type: string; className?: string }> = ({ type, className = "" }) => {
  const iconPath = SETTINGS_ICON_PATHS[type as keyof typeof SETTINGS_ICON_PATHS] || SETTINGS_ICON_PATHS.settings;

  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={iconPath} />
    </svg>
  );
};

interface NavGroup {
  label: string;
  icon: string;
  items: SettingsSection[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Equipment',
    icon: 'solar',
    items: ['modules', 'inverters', 'optimizers', 'combiners', 'rsds', 'batteries'],
  },
  {
    label: 'Electrical',
    icon: 'electric',
    items: ['msp', 'poi', 'disco', 'conduit', 'electric'],
  },
  {
    label: 'Structural',
    icon: 'structural',
    items: ['structural', 'mounting'],
  },
  {
    label: 'Building',
    icon: 'building',
    items: ['roofing', 'building'],
  },
  {
    label: 'Settings',
    icon: 'settings',
    items: ['general-settings', 'client-logos'],
  },
];

export const SettingsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [activeSection, setActiveSection] = useState<SettingsSection>('modules');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    Equipment: true,
    Electrical: false,
    Structural: false,
    Building: false,
    Settings: false,
  });

  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupLabel]: !prev[groupLabel],
    }));
  };

  const is_initialized = useAppSelector(selectIsInitialized);

  const moduleBrands = useAppSelector(selectModuleBrands);
  const solarModules = useAppSelector(selectSolarModules);
  const inverterBrands = useAppSelector(selectInverterBrands);
  const inverters = useAppSelector(selectInverters);
  const optimizers = useAppSelector(selectOptimizers);
  const combiners = useAppSelector(selectCombiners);
  const rsds = useAppSelector(selectRsds);
  const batteryBrands = useAppSelector(selectBatteryBrands);
  const batteries = useAppSelector(selectBatteries);
  const mspBusses = useAppSelector(selectMspBusses);
  const mspBreakers = useAppSelector(selectMspBreakers);
  const mspManufacturers = useAppSelector(selectMspManufacturers);
  const mspLocations = useAppSelector(selectMspLocations);
  const mspStyles = useAppSelector(selectMspStyles);
  const mspTypes = useAppSelector(selectMspTypes);
  const poiMethods = useAppSelector(selectPoi);
  const poiLocations = useAppSelector(selectPoiLocations);
  const atsBreakerSizes = useAppSelector(selectAtsBreakerSizes);
  const discoSwitches = useAppSelector(selectDisco);
  const conduitSizes = useAppSelector(selectConduitSizes);
  const conduitTypes = useAppSelector(selectConduitTypes);
  const structuralFramingSizes = useAppSelector(selectStructuralFramingSizes);
  const structuralFramingTypes = useAppSelector(selectStructuralFramingTypes);
  const structuralRafterSpacings = useAppSelector(selectStructuralRafterSpacings);
  const attachments = useAppSelector(selectAttachments);
  const rails = useAppSelector(selectRails);
  const roofTypes = useAppSelector(selectRoofTypes);
  const roofConditions = useAppSelector(selectRoofConditions);
  const reroofStatuses = useAppSelector(selectReroofStatuses);
  const buildingHeights = useAppSelector(selectBuildingHeights);
  const electricConfigs = useAppSelector(selectElectricConfigs);
  const settings = useAppSelector(selectSettings);

  useEffect(() => {
    if (!is_initialized) {
      dispatch(fetchAllDatabaseData());
    }
  }, [dispatch, is_initialized]);

  const allSections = getEnabledSettingsSections();

  const getStats = (sectionId: SettingsSection): string => {
    switch (sectionId) {
      case 'modules':
        return `${moduleBrands.length} Brands, ${solarModules.length} Models`;
      case 'inverters':
        return `${inverterBrands.length} Brands, ${inverters.length} Models`;
      case 'optimizers':
        return `${optimizers.length} Types`;
      case 'combiners':
        return `${combiners.length} Models`;
      case 'rsds':
        return `${rsds.length} Types`;
      case 'batteries':
        return `${batteryBrands.length} Brands, ${batteries.length} Models`;
      case 'msp':
        const totalMsp = mspBusses.length + mspBreakers.length + mspManufacturers.length + mspLocations.length + mspStyles.length + mspTypes.length;
        return `${totalMsp} Items`;
      case 'poi':
        const totalPoi = poiMethods.length + poiLocations.length + atsBreakerSizes.length;
        return `${totalPoi} Items`;
      case 'disco':
        return `${discoSwitches.length} Switches`;
      case 'conduit':
        return `${conduitSizes.length + conduitTypes.length} Items`;
      case 'electric':
        return `${electricConfigs.length} Configs`;
      case 'structural':
        const totalStructural = structuralFramingSizes.length + structuralFramingTypes.length + structuralRafterSpacings.length;
        return `${totalStructural} Items`;
      case 'mounting':
        return `${attachments.length} Attachments, ${rails.length} Rails`;
      case 'roofing':
        return `${roofTypes.length + roofConditions.length + reroofStatuses.length} Items`;
      case 'building':
        return `${buildingHeights.length} Heights`;
      case 'settings':
        return `${settings.length} Settings`;
      case 'general-settings':
        const generalSettings = settings.filter(s => s.type !== 'client_logos');
        return `${generalSettings.length} Settings`;
      case 'client-logos':
        const clientLogosSetting = settings.find(s => s.type === 'client_logos');
        const logoCount = clientLogosSetting?.value ? Object.keys(clientLogosSetting.value as Record<string, string>).length : 0;
        return `${logoCount} Mappings`;
      default:
        return '';
    }
  };

  const getSectionConfig = (id: SettingsSection) => allSections.find(s => s.id === id);

  const renderContent = () => {
    switch (activeSection) {
      case 'modules':
        return <ModuleManagement />;
      case 'inverters':
        return <InverterManagement />;
      case 'optimizers':
        return <OptimizerManagement />;
      case 'combiners':
        return <CombinerManagement />;
      case 'rsds':
        return <RsdManagement />;
      case 'batteries':
        return <BatteryManagement />;
      case 'structural':
        return <StructuralManagement />;
      case 'msp':
        return <MspManagement />;
      case 'poi':
        return <PoiManagement />;
      case 'disco':
        return <DiscoManagement />;
      case 'conduit':
        return <ConduitManagement />;
      case 'mounting':
        return <AttachmentRailManagement />;
      case 'roofing':
        return <RoofManagement />;
      case 'building':
        return <BuildingHeightManagement />;
      case 'electric':
        return <ElectricConfigManagement />;
      case 'settings':
        return <SettingsManagement />;
      case 'general-settings':
        return <SettingsManagement showOnlyGeneral />;
      case 'client-logos':
        return <ClientLogosManagement />;
      default:
        return (
          <div className="p-8 text-center text-slate-600">
            <h3 className="text-lg font-semibold mb-2">Coming Soon</h3>
            <p>This section is under development.</p>
          </div>
        );
    }
  };

  const currentSection = getSectionConfig(activeSection);

  return (
    <div className="h-[calc(100vh-64px)] overflow-hidden">
      <div className="mx-[9%] my-4 h-[calc(100%-2rem)] flex flex-col">
        {/* Full Width Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60 border-b-0 rounded-t-3xl flex items-center gap-4">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-sm">
            <AdminIcon type="settings" className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800 tracking-wide">Settings</h1>
            <p className="text-xs text-amber-700">Manage system configuration</p>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex flex-1 bg-white/80 rounded-b-3xl shadow-sm border border-t-0 border-slate-200/60 overflow-hidden">
          {/* Left Sidebar */}
          <aside className="w-56 bg-white/90 border-r border-slate-200/60 flex flex-col overflow-hidden">
            {/* Navigation */}
            <nav className="flex-1 overflow-auto py-3 bg-slate-50/50">
          {NAV_GROUPS.map((group) => {
            const enabledItems = group.items.filter(id => allSections.some(s => s.id === id));
            if (enabledItems.length === 0) return null;

            const isExpanded = expandedGroups[group.label] ?? true;

            return (
              <div key={group.label} className="mb-3 px-2">
                <button
                  onClick={() => toggleGroup(group.label)}
                  className="w-full px-2.5 py-2 flex items-center gap-2 bg-slate-100 hover:bg-slate-200 rounded-md transition-all duration-200 cursor-pointer"
                >
                  <AdminIcon type={group.icon} className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-bold uppercase tracking-wide flex-1 text-left text-slate-600">
                    {group.label}
                  </span>
                  <svg
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <ul className={`overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-[500px] opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
                  {enabledItems.map((sectionId) => {
                    const section = getSectionConfig(sectionId);
                    if (!section) return null;
                    const isActive = activeSection === sectionId;

                    return (
                      <li key={sectionId}>
                        <button
                          onClick={() => setActiveSection(sectionId)}
                          className={`w-full flex items-center gap-2.5 px-2.5 py-2 text-left transition-all duration-150 rounded-md ${
                            isActive
                              ? 'bg-amber-100 text-amber-800'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                        >
                          <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-amber-200' : 'bg-slate-100'}`}>
                            <AdminIcon
                              type={section.icon}
                              className={`w-4 h-4 ${isActive ? 'text-amber-700' : 'text-slate-500'}`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-[13px] font-medium truncate ${isActive ? 'text-amber-800' : 'text-slate-700'}`}>
                              {section.label}
                            </div>
                            <div className={`text-[11px] truncate ${isActive ? 'text-amber-600' : 'text-slate-400'}`}>
                              {getStats(sectionId)}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 flex flex-col overflow-hidden bg-slate-50/50">
            {/* Content Header */}
            {currentSection && (
              <header className="bg-white/70 border-b border-slate-200/60 px-6 py-3">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 bg-gradient-to-br ${currentSection.color} rounded-lg flex items-center justify-center shadow-sm`}>
                    <AdminIcon type={currentSection.icon} className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900">{currentSection.label}</h2>
                    <p className="text-xs text-slate-500">{currentSection.description}</p>
                  </div>
                </div>
              </header>
            )}

            {/* Content Body */}
            <div className="flex-1 overflow-auto">
              {renderContent()}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};
